
export const FORM_FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'checkbox',
  'select',
  'multiselect',
  'email',
  'phone',
  'url',
  'directory',
  'folder',
  'section',
  'time',
  'group',
  'note',
  'user',
  'file',
] as const;

export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const GROUP_COLUMN_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'time',
  'checkbox',
  'select',
  'email',
  'phone',
  'url',
  'user',
] as const;

export type GroupColumnType = (typeof GROUP_COLUMN_TYPES)[number];

export const DEFAULT_GROUP_ROWS = 5;
export const MAX_GROUP_ROWS = 20;
export const MAX_GROUP_COLUMNS = 6;
export const MAX_NOTE_LENGTH = 4000;
export const MAX_HINT_LENGTH = 1000;
export const DEFAULT_FORM_FILES = 5;
export const MAX_FORM_FILES = 10;

export interface FormFileValue {
  id: string;
  name: string;
  size?: number;
  mime?: string;
  url?: string;
}

export function formFileValues(value: unknown): FormFileValue[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (v): v is FormFileValue =>
      typeof v === 'object' &&
      v !== null &&
      typeof (v as { id?: unknown }).id === 'string' &&
      typeof (v as { name?: unknown }).name === 'string',
  );
}

export function isFileField(field: { type: FormFieldType }): boolean {
  return field.type === 'file';
}

export interface GroupColumn {
  key: string;
  type: GroupColumnType;
  label: string;
  required?: boolean;
  placeholder?: string;
  options?: FormFieldOption[];
}

export type GroupRow = Record<string, unknown>;

export const VISIBILITY_OPS = ['eq', 'ne', 'in', 'filled', 'empty', 'gt', 'lt'] as const;
export type VisibilityOp = (typeof VISIBILITY_OPS)[number];

export const UNARY_OPS: readonly VisibilityOp[] = ['filled', 'empty'];

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormFieldCondition {
  field: string;
  op?: VisibilityOp;
  value?: string | number | boolean | string[];
  equals?: string | number | boolean;
}

export interface FormFieldDisplay {
  search?: 'auto' | 'always' | 'never';
  groupBy?: string;
  multiple?: boolean;
}

export interface FormField {
  key: string;
  type: FormFieldType;
  label: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  hintAs?: 'text' | 'tooltip';
  options?: FormFieldOption[];
  dependsOn?: { field: string; mapping: Record<string, string[]> };
  visibleIf?: FormFieldCondition;
  source?: { directoryId: string; parentFromField?: string };
  display?: FormFieldDisplay;
  allowOther?: boolean;
  personnelNumber?: PersonnelNumberMode;
  affectsWeight?: boolean;
  content?: string;
  columns?: GroupColumn[];
  maxRows?: number;
  maxFiles?: number;
  submitOnly?: boolean;
}

export interface DirectoryValue {
  id?: string;
  label: string;
  custom?: boolean;
}

export const FOLDER_ACCESS_TYPES = ['read', 'write', 'full'] as const;
export type FolderAccessType = (typeof FOLDER_ACCESS_TYPES)[number];

export const FOLDER_ACCESS_LABELS: Record<FolderAccessType, string> = {
  read: 'Чтение',
  write: 'Чтение и запись',
  full: 'Полный доступ',
};

export interface FolderSubject {
  id?: string;
  label: string;
  external?: boolean;
}

export interface FolderValue {
  nodeId?: string;
  path: string;
  accessType?: FolderAccessType;
  subjects?: FolderSubject[];
}

export const MAX_FOLDER_SUBJECTS = 25;

export interface FormSchema {
  fields: FormField[];
}

export type FormValues = Record<string, unknown>;


export function isLayoutField(field: { type: FormFieldType }): boolean {
  return field.type === 'section' || field.type === 'note';
}

export function isGroupField(field: { type: FormFieldType }): boolean {
  return field.type === 'group';
}

export function isUserField(field: { type: FormFieldType }): boolean {
  return field.type === 'user';
}

export interface UserFieldValue {
  id?: string;
  label: string;
  external?: boolean;
  personnelNumber?: string;
}

export type PersonnelNumberMode = 'optional' | 'required';
export const MAX_PERSONNEL_NUMBER = 20;

export function userValueText(v: UserFieldValue): string {
  const notes = [
    v.personnelNumber ? `таб. № ${v.personnelNumber}` : null,
    v.external ? 'нет в системе' : null,
  ].filter(Boolean);
  return notes.length > 0 ? `${v.label} (${notes.join(', ')})` : v.label;
}

export function userFieldValues(value: unknown): UserFieldValue[] {
  if (Array.isArray(value)) return value.filter(isDirectoryValue);
  return isDirectoryValue(value) ? [value] : [];
}

export function groupRows(value: unknown): GroupRow[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (row): row is GroupRow => typeof row === 'object' && row !== null && !Array.isArray(row),
  );
}

export function isGroupRowFilled(columns: GroupColumn[], row: GroupRow): boolean {
  return columns.some((c) => c.type !== 'checkbox' && !isEmptyValue(row[c.key]));
}

export function isRealTime(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

export function isChoiceField(field: { type: FormFieldType }): boolean {
  return field.type === 'select' || field.type === 'multiselect';
}

export function isDirectoryField(field: { type: FormFieldType }): boolean {
  return field.type === 'directory';
}

export function isFolderField(field: { type: FormFieldType }): boolean {
  return field.type === 'folder';
}

export function isFolderValue(value: unknown): value is FolderValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { path?: unknown }).path === 'string'
  );
}

export function folderValues(value: unknown): FolderValue[] {
  if (Array.isArray(value)) return value.filter(isFolderValue);
  return isFolderValue(value) ? [value] : [];
}

export function isDirectoryValue(value: unknown): value is DirectoryValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { label?: unknown }).label === 'string'
  );
}

export function directoryValues(value: unknown): DirectoryValue[] {
  if (Array.isArray(value)) return value.filter(isDirectoryValue);
  return isDirectoryValue(value) ? [value] : [];
}

export function isPatternField(field: { type: FormFieldType }): boolean {
  return field.type === 'email' || field.type === 'phone' || field.type === 'url';
}

export function conditionOp(cond: FormFieldCondition): VisibilityOp {
  return cond.op ?? 'eq';
}

export function conditionValue(cond: FormFieldCondition): unknown {
  return cond.op ? cond.value : cond.equals;
}

export function isEmptyValue(value: unknown): boolean {
  return (
    value == null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  );
}

function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  return Boolean(value);
}

function sameValue(actual: unknown, expected: unknown): boolean {
  if (actual == null || expected == null) return false;
  if (Array.isArray(actual)) return actual.some((v) => sameValue(v, expected));
  if (isDirectoryValue(actual)) return sameValue(actual.id ?? actual.label, expected);
  if (typeof actual === 'boolean' || typeof expected === 'boolean') {
    return toBool(actual) === toBool(expected);
  }
  if (typeof actual === 'number' || typeof expected === 'number') {
    const a = Number(actual);
    const b = Number(expected);
    return Number.isFinite(a) && Number.isFinite(b) && a === b;
  }
  return String(actual) === String(expected);
}

function isConditionMet(cond: FormFieldCondition | undefined, values: FormValues): boolean {
  if (!cond) return true;
  const actual = values[cond.field];
  const op = conditionOp(cond);
  const expected = conditionValue(cond);

  switch (op) {
    case 'filled':
      return !isEmptyValue(actual);
    case 'empty':
      return isEmptyValue(actual);
    case 'ne':
      return !sameValue(actual, expected);
    case 'in': {
      const list = Array.isArray(expected) ? expected : [expected];
      return list.some((v) => sameValue(actual, v));
    }
    case 'gt':
    case 'lt': {
      const a = Number(actual);
      const b = Number(expected);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
      return op === 'gt' ? a > b : a < b;
    }
    case 'eq':
    default:
      return sameValue(actual, expected);
  }
}

export interface ResolvedForm {
  visibleKeys: string[];
  optionsByKey: Record<string, FormFieldOption[]>;
  values: FormValues;
}

export function resolveForm(schema: FormSchema, raw: FormValues): ResolvedForm {
  const visibleKeys: string[] = [];
  const optionsByKey: Record<string, FormFieldOption[]> = {};
  const values: FormValues = {};
  const indexByKey = new Map(schema.fields.map((f, i) => [f.key, i]));

  const parentValue = (selfIndex: number, parentKey: string): unknown => {
    const parentIndex = indexByKey.get(parentKey);
    return parentIndex !== undefined && parentIndex > selfIndex ? raw[parentKey] : values[parentKey];
  };

  for (let i = 0; i < schema.fields.length; i++) {
    const field = schema.fields[i];
    const scope = field.visibleIf
      ? { ...values, [field.visibleIf.field]: parentValue(i, field.visibleIf.field) }
      : values;
    if (!isConditionMet(field.visibleIf, scope)) continue;
    visibleKeys.push(field.key);

    if (isLayoutField(field)) continue;

    if (isChoiceField(field)) {
      const options = field.options ?? [];
      let allowed = options;
      if (field.dependsOn) {
        const parentValue_ = parentValue(i, field.dependsOn.field);
        const permitted =
          typeof parentValue_ === 'string' ? (field.dependsOn.mapping[parentValue_] ?? []) : [];
        allowed = options.filter((o) => permitted.includes(o.value));
      }
      optionsByKey[field.key] = allowed;

      const allowedValues = new Set(allowed.map((o) => o.value));
      const submitted = raw[field.key];
      if (field.type === 'multiselect') {
        const picked = (Array.isArray(submitted) ? submitted : submitted == null ? [] : [submitted])
          .map(String)
          .filter((v) => allowedValues.has(v));
        if (picked.length > 0) values[field.key] = picked;
      } else if (typeof submitted === 'string' && allowedValues.has(submitted)) {
        values[field.key] = submitted;
      }
      continue;
    }

    if (isFolderField(field)) {
      const pickedFolders = folderValues(raw[field.key]);
      if (pickedFolders.length === 0) continue;
      values[field.key] = field.display?.multiple ? pickedFolders : pickedFolders[0];
      continue;
    }

    if (isUserField(field)) {
      const picked = userFieldValues(raw[field.key]);
      if (picked.length === 0) continue;
      values[field.key] = field.display?.multiple ? picked : picked[0];
      continue;
    }

    if (isFileField(field)) {
      const files = formFileValues(raw[field.key]);
      if (files.length > 0) values[field.key] = files;
      continue;
    }

    if (isDirectoryField(field)) {
      const parentKey = field.source?.parentFromField;
      if (parentKey) {
        const parent = directoryValues(parentValue(i, parentKey))[0];
        if (!parent?.id) continue;
      }
      const picked = directoryValues(raw[field.key]);
      if (picked.length === 0) continue;
      values[field.key] = field.display?.multiple ? picked : picked[0];
      continue;
    }

    const value = raw[field.key];
    if (!isEmptyValue(value)) values[field.key] = value;
    else if (field.type === 'checkbox') values[field.key] = value === true;
  }

  return { visibleKeys, optionsByKey, values };
}

const PATTERNS: Record<'email' | 'phone' | 'url', { test: (v: string) => boolean; error: string }> = {
  email: {
    test: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v),
    error: 'укажите e-mail',
  },
  phone: {
    test: (v) => /^\+?[\d\s()-]{5,20}$/.test(v) && (v.match(/\d/g)?.length ?? 0) >= 5,
    error: 'укажите номер телефона',
  },
  url: {
    test: (v) => {
      try {
        const u = new URL(v);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch {
        return false;
      }
    },
    error: 'укажите ссылку (http:// или https://)',
  },
};

export function fieldFormatError(field: FormField, value: unknown): string | null {
  if (!isPatternField(field)) return null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const pattern = PATTERNS[field.type as 'email' | 'phone' | 'url'];
  return pattern.test(value.trim()) ? null : pattern.error;
}

export function validateFormValues(schema: FormSchema, values: FormValues): string[] {
  const errors: string[] = [];
  const resolved = resolveForm(schema, values);
  const visible = new Set(resolved.visibleKeys);

  for (const field of schema.fields) {
    if (!visible.has(field.key) || isLayoutField(field)) continue;
    const value = resolved.values[field.key];

    if (isGroupField(field)) {
      errors.push(...groupErrors(field, value));
      continue;
    }

    if (isFileField(field)) {
      const files = formFileValues(value);
      if (field.required && files.length === 0) errors.push(`Поле «${field.label}»: приложите файл`);
      const limit = field.maxFiles ?? DEFAULT_FORM_FILES;
      if (files.length > limit) errors.push(`Поле «${field.label}»: файлов больше ${limit}`);
      continue;
    }

    if (field.required) {
      const empty = field.type === 'checkbox' ? value !== true : isEmptyValue(value);
      if (empty) {
        errors.push(`Заполните поле «${field.label}»`);
        continue;
      }
    }
    if (field.type === 'time' && typeof value === 'string' && value.trim() !== '' && !isRealTime(value.trim())) {
      errors.push(`Поле «${field.label}»: укажите время чч:мм`);
      continue;
    }
    if (isFolderField(field)) {
      const pickedFolders = folderValues(values[field.key]);
      if (!field.display?.multiple && pickedFolders.length > 1) {
        errors.push(`Поле «${field.label}»: допустима одна папка`);
      }
      if (pickedFolders.some((v) => (v.subjects?.length ?? 0) > MAX_FOLDER_SUBJECTS)) {
        errors.push(`Поле «${field.label}»: получателей больше ${MAX_FOLDER_SUBJECTS}`);
      }
      continue;
    }

    if (isUserField(field)) {
      const picked = userFieldValues(value);
      if (!field.display?.multiple && picked.length > 1) {
        errors.push(`Поле «${field.label}»: допустим один сотрудник`);
      }
      if (!field.allowOther && picked.some((v) => !v.id)) {
        errors.push(`Поле «${field.label}»: выберите сотрудника из списка`);
      }
      if (field.personnelNumber === 'required') {
        for (const person of picked.filter((v) => !v.personnelNumber?.trim())) {
          errors.push(`Поле «${field.label}»: укажите табельный номер — ${person.label}`);
        }
      }
      continue;
    }

    if (isDirectoryField(field)) {
      const picked = directoryValues(value);
      if (!field.display?.multiple && picked.length > 1) {
        errors.push(`Поле «${field.label}»: допустимо одно значение`);
      }
      if (!field.allowOther && picked.some((v) => v.custom)) {
        errors.push(`Поле «${field.label}»: свой вариант не разрешён`);
      }
      continue;
    }
    const formatError = fieldFormatError(field, value);
    if (formatError) errors.push(`Поле «${field.label}»: ${formatError}`);
  }
  return errors;
}

function groupErrors(field: FormField, value: unknown): string[] {
  const columns = field.columns ?? [];
  const rows = groupRows(value).filter((row) => isGroupRowFilled(columns, row));
  const errors: string[] = [];
  if (field.required && rows.length === 0) {
    errors.push(`Поле «${field.label}»: заполните хотя бы одну строку`);
  }
  const limit = field.maxRows ?? DEFAULT_GROUP_ROWS;
  if (rows.length > limit) errors.push(`Поле «${field.label}»: строк больше ${limit}`);
  rows.forEach((row, i) => {
    for (const column of columns) {
      const at = `Поле «${field.label}», строка ${i + 1}, «${column.label}»`;
      const cell = row[column.key];
      if (column.type === 'checkbox') continue;
      if (isEmptyValue(cell)) {
        if (column.required) errors.push(`${at}: заполните`);
        continue;
      }
      if (column.type === 'time' && !isRealTime(String(cell).trim())) {
        errors.push(`${at}: укажите время чч:мм`);
        continue;
      }
      const formatError = fieldFormatError(column, cell);
      if (formatError) errors.push(`${at}: ${formatError}`);
    }
  });
  return errors;
}

export function formatGroupCell(column: GroupColumn, value: unknown): string | null {
  if (column.type === 'checkbox') return value === true ? 'Да' : 'Нет';
  if (isEmptyValue(value)) return null;
  if (column.type === 'user') return userFieldValues(value)[0]?.label ?? null;
  if (column.type === 'select') return column.options?.find((o) => o.value === value)?.label ?? String(value);
  if (column.type === 'date') return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU');
  return String(value);
}

export function formatFormValue(field: FormField, value: unknown): string | null {
  if (isLayoutField(field)) return null;
  if (field.type === 'checkbox' && field.submitOnly) return null;
  if (isEmptyValue(value)) return null;
  const optionLabel = (v: string) => field.options?.find((o) => o.value === v)?.label ?? v;
  switch (field.type) {
    case 'checkbox':
      return value === true ? 'Да' : 'Нет';
    case 'select':
      return optionLabel(String(value));
    case 'multiselect':
      return (value as string[]).map(optionLabel).join(', ');
    case 'folder':
      return folderValues(value)
        .map((v) => {
          const access = v.accessType ? ` (${FOLDER_ACCESS_LABELS[v.accessType]})` : '';
          const people = v.subjects?.length
            ? ` — ${v.subjects.map((s) => s.label).join(', ')}`
            : '';
          return `${v.path}${access}${people}`;
        })
        .join('; ');
    case 'file':
      return formFileValues(value)
        .map((f) => f.name)
        .join(', ');
    case 'user':
      return userFieldValues(value).map(userValueText).join(', ');
    case 'directory':
      return directoryValues(value)
        .map((v) => v.label)
        .join(', ');
    case 'date':
      return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU');
    case 'group': {
      const columns = field.columns ?? [];
      const lines = groupRows(value)
        .filter((row) => isGroupRowFilled(columns, row))
        .map((row, i) => {
          const cells = columns
            .map((c) => {
              const text = formatGroupCell(c, row[c.key]);
              return text === null ? null : `${c.label}: ${text}`;
            })
            .filter((c): c is string => c !== null);
          return `${i + 1}) ${cells.join('; ')}`;
        });
      return lines.length > 0 ? lines.join('\n') : null;
    }
    default:
      return String(value);
  }
}
